export { getLanguageCode } from './locale';
export { escapeHtml } from './shared.mjs';
import {
  LEGAL_PAGES,
  COOKIE_SETTINGS_LABEL,
  legalPageFromPath,
} from './constants';

export const getCssVariable = (key) => {
  const computedStyle = getComputedStyle(document.documentElement);
  return computedStyle.getPropertyValue(key).trim();
};

export const isLandscape = () => window.innerWidth >= window.innerHeight;

export const prefersReducedMotion = () =>
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const debounce = (fn, wait) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
};

export const scrollToTop = () => {
  if (prefersReducedMotion()) {
    window.scrollTo(0, 0);
    return;
  }
  const c = document.documentElement.scrollTop || document.body.scrollTop;
  if (c > 0) {
    window.requestAnimationFrame(scrollToTop);
    window.scrollTo(0, c - c / 4);
  }
};

export const isIOS = () =>
  [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod',
  ].includes(navigator.platform) ||
  // iPad on iOS 13 detection
  (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

export const unwrapNoscript = (error) => {
  document.documentElement.classList.add('no-js');
  document.querySelector('div.loading-screen').classList.add('hidden');
  const unwrap = (wrapper) => {
    const docFrag = document.createDocumentFragment();
    while (wrapper.firstChild) {
      const span = document.createElement('span');
      span.innerHTML = wrapper.removeChild(wrapper.firstChild).textContent;
      docFrag.appendChild(span);
    }
    wrapper.parentNode.replaceChild(docFrag, wrapper);
  };
  const noscript = document.querySelectorAll('noscript');
  for (let i = 0; i < noscript.length; i++) {
    unwrap(noscript[i]);
  }
  window.scrollTo(0, 0);
  if (error) {
    const warning = document.querySelector('code.warning');
    if (warning) {
      warning.textContent =
        'Something went wrong while loading the page. Please reload.';
    }
  }
};

export const getMicrocopySlots = () =>
  Array.from(document.querySelectorAll('.microcopy'));

// Both slugs resolve on both hosts; this just keeps German URLs on the
// German site.
export const localizeLegalLinks = (lang) => {
  const language = LEGAL_PAGES.impressum.slug[lang] ? lang : 'en';
  document.querySelectorAll('.legal-links a').forEach((a) => {
    const page = legalPageFromPath(a.getAttribute('href') || '');
    if (page) a.setAttribute('href', LEGAL_PAGES[page].slug[language]);
  });
  const cookieLink = document.querySelector('.legal-links .cookie-settings');
  if (cookieLink) cookieLink.textContent = COOKIE_SETTINGS_LABEL[language];
};

export const onError = (error) => {
  console.error(error);
  unwrapNoscript(error);
};

export const useDarkMode = () => {
  if (!window.matchMedia) return;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  if (mq.matches) {
    document.documentElement.classList.add('dark-mode');
  }
  mq.addEventListener('change', (e) => {
    document.documentElement.classList.toggle('dark-mode', e.matches);
    setDarkModeMessage();
  });
};

export const setDarkModeMessage = () => {
  const isDarkMode = document.documentElement.classList.contains('dark-mode');
  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) {
    themeColor.setAttribute('content', isDarkMode ? '#1d1d1d' : '#fdfdfd');
  }
  // The legal pages have no toolbar.
  const inverterButton = document.querySelector('button.inverter');
  if (!inverterButton) return;
  const lang = document.documentElement.lang;
  const message =
    lang === 'de'
      ? `Zu ${isDarkMode ? 'hellem' : 'dunklem'} Modus wechseln`
      : `Switch to ${isDarkMode ? 'light' : 'dark'} mode`;
  inverterButton.setAttribute('aria-label', message);
  inverterButton.setAttribute('title', message);
};
